import React, { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { toPng } from "html-to-image";
import { Download, ArrowRight, Loader2 } from "lucide-react";
import EnrollmentCertificate, { CERT_W, CERT_H } from "../components/hub/EnrollmentCertificate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Public share page (+ ?render=1 raw mode used by the backend to capture the email attachment).
export default function CertificatePage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const raw = params.get("render") === "1";
  const [cert, setCert] = useState(null);
  const [err, setErr] = useState("");
  const [ready, setReady] = useState(false);
  const [scale, setScale] = useState(0.5);
  const [busy, setBusy] = useState(false);
  const node = useRef(null);

  useEffect(() => {
    axios.get(`${API}/certificate/${id}`).then(({ data }) => setCert(data)).catch(() => setErr("This certificate could not be found."));
  }, [id]);
  useEffect(() => {
    if (!cert) return;
    const t = setTimeout(() => document.fonts.ready.then(() => setReady(true)), 900);
    return () => clearTimeout(t);
  }, [cert]);
  useEffect(() => {
    if (raw) return;
    const fit = () => setScale(Math.min(1, (window.innerWidth - 32) / CERT_W));
    fit(); window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [raw]);

  const download = async () => {
    setBusy(true);
    try {
      await document.fonts.ready;
      const url = await toPng(node.current, { pixelRatio: 2, cacheBust: true, width: CERT_W, height: CERT_H, style: { transform: "none" } });
      const a = document.createElement("a"); a.href = url; a.download = `NextO-Enrollment-Certificate-${cert.certificate_id}.png`; a.click();
    } catch { toast.error("Couldn't render the certificate."); }
    finally { setBusy(false); }
  };

  if (err) return <div data-testid="certificate-not-found" className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center font-mono text-sm uppercase tracking-widest text-white/60">{err}</div>;
  if (!cert) return <div className="min-h-screen bg-[#0a0a0a]" />;
  const art = <EnrollmentCertificate name={cert.player_name} certId={cert.certificate_id} enrolledAt={cert.enrolled_at} volume={cert.volume} ready={ready} />;
  if (raw) return <div className="bg-[#050505] inline-block" ref={node}>{art}</div>;

  return (
    <div data-testid="certificate-page" className="min-h-screen bg-[#0a0a0a] text-white flex flex-col items-center py-10 px-4">
      <div className="font-mono text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-5">The Next O Technical Academy · Verified Enrollment</div>
      <div className="overflow-hidden rounded-lg shadow-[0_40px_120px_rgba(0,0,0,0.8)]" style={{ width: CERT_W * scale, height: CERT_H * scale }}>
        <div ref={node} style={{ width: CERT_W, height: CERT_H, transform: `scale(${scale})`, transformOrigin: "top left" }}>{art}</div>
      </div>
      <div className="flex flex-wrap items-center justify-center gap-3 mt-7">
        <button data-testid="public-cert-download-btn" onClick={download} disabled={busy} className="inline-flex items-center gap-2 bg-[#D4AF37] text-black text-xs font-bold uppercase tracking-wider px-5 py-3 rounded-sm disabled:opacity-60">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Download PNG
        </button>
        <button data-testid="public-cert-enroll-btn" onClick={() => navigate("/")} className="inline-flex items-center gap-2 bg-[#C41E24] hover:bg-[#A0161D] text-white text-xs font-bold uppercase tracking-wider px-5 py-3 rounded-sm transition-colors">
          Enroll in the Blueprint <ArrowRight size={14} />
        </button>
      </div>
    </div>
  );
}
